// feature/role-views — Revisor de Fallos pending list (Molly's primary view).
// SPEC §9.4: only fallos awaiting decision, filtered by tipo_fallo.
// feature/tipo-fallo-tabs. The old "Tipo de fallo" dropdown
// (5 separate enum values incl. legacy 1ª/2ª split) was replaced by the
// shared TipoFalloTabs component (Todos / Favorables / Desfavorables /
// Nulidad / Sin clasificar). The list endpoint now returns
// case_type_data.tipo_fallo so the tabs can compute counts client-side
// without an N-row drill-in.
import React, { useEffect, useState } from "react";
import { cases as casesApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import CaseDetail from "../case-detail";
import TipoFalloTabs, { filterByTipoFallo } from "../../components/TipoFalloTabs";
import EstadoFilterPills, { filterByActiveStatus } from "../../components/EstadoFilterPills";
// feature/tipo-fallo-humanization. Replaces the raw-enum
// badge ("FAVORABLE_1A") that Molly used to read on every row.
import { TipoFalloPill, EstadoPill } from "../../components/Pills";
// feature/fallos-grouped-view.
import FallosViewToggle from "../../components/fallos/FallosViewToggle";
import GroupedFallosView from "../../components/fallos/GroupedFallosView";
import { useAuth } from "../../auth/AuthContext";

// Legacy badge style map kept (other modules import it). Sprint 55 doesn't
// use it on this page anymore, but historic + Decisión-tab code still does.
const TIPO_FALLO_OPTS = [
  ["", "Todos"],
  ["FAVORABLE_1A",     "Favorable 1ª"],
  ["DESFAVORABLE_1A",  "Desfavorable 1ª"],
  ["FAVORABLE_2A",     "Favorable 2ª"],
  ["DESFAVORABLE_2A",  "Desfavorable 2ª"],
  ["NO_PEDIR_INSUMO",  "No pedir insumo"],
];

const TF_STYLE = {
  FAVORABLE_1A:     "success",
  FAVORABLE_2A:     "success",
  DESFAVORABLE_1A:  "danger",
  DESFAVORABLE_2A:  "danger",
  NO_PEDIR_INSUMO:  "muted",
  NULIDAD:          "warning",
};

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

const VIEW_MODE_KEY = "eds_fallos_view_mode";

export default function Fallos() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("todos");
  // feature/estado-filter-pills. Default to active so
  // historical decided fallos don't crowd Molly's pending queue.
  const [estadoFilter, setEstadoFilter] = useState("activos");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  // feature/fallos-grouped-view. Persist the toggle so
  // Molly's preference sticks across sessions. Plain global key (not
  // per-user) is fine — only one revisor_fallos uses this page today,
  // and the value is just "flat" or "grouped".
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem(VIEW_MODE_KEY);
      return v === "grouped" ? "grouped" : "flat";
    } catch (_) { return "flat"; }
  });
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); }
    catch (_) { /* ignore */ }
  }, [viewMode]);

  function load() {
    setData(null);
    // feature/fallos-workflow-inversion. Molly's queue feeds from BOTH
    // fallo_pendiente_revision (legacy: fallos that skipped insumos at intake)
    // and pendiente_revision_impugnacion (new: handed off after insumos prep).
    // feature/desfavorable-parallel-decision. Sprint 58d made
    // Molly the abogado_asignado_id for desfavorables from creation, so
    // they should also appear here while still in pipeline estados (the
    // "parallel visibility" intent half-shipped in 58d). Without this
    // widening, 122 active desfavorables were structurally invisible.
    // tipo-aware filter:
    //   • Favorables / Nulidad / unclassified → review estados only.
    //   • Desfavorables → also include pipeline estados so Molly can
    //     decide impugnación in parallel with insumos team prep.
    // Backend filters by tipo=fallo via the role-based visibility rule.
    const PIPELINE_FOR_DESFAVORABLE = new Set([
      "asignado_abogado",
      "pendiente_insumos",
      "insumos_solicitados",
      "expediente_solicitado",
      "insumos_recibidos",
      "pendiente_revision_impugnacion",
    ]);
    const REVIEW_ESTADOS = new Set([
      "fallo_pendiente_revision",
      "pendiente_revision_impugnacion",
    ]);
    casesApi.list({ tipo: "fallo" })
      .then(r => setData((r.data || []).filter((c) => {
        const isDesfavorable = c.tipo_fallo === "DESFAVORABLE_1A" || c.tipo_fallo === "DESFAVORABLE_2A";
        return isDesfavorable
          ? PIPELINE_FOR_DESFAVORABLE.has(c.estado)
          : REVIEW_ESTADOS.has(c.estado);
      })))
      .catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);

  const fallosCases = data || [];
  // Sprint 55c — apply the active/closed slice first so the
  // TipoFalloTabs / grouped sections counts reflect the visible scope.
  const estadoFilteredCases = filterByActiveStatus(fallosCases, estadoFilter);
  // Sprint 61 — TipoFalloTabs is the implicit grouping in flat mode;
  // grouped mode buckets by tipo_fallo natively, so the tab filter is
  // hidden there to avoid double-filtering.
  const visibleCases = viewMode === "flat"
    ? filterByTipoFallo(estadoFilteredCases, activeTab)
    : estadoFilteredCases;

  return (
    <div className="col" style={{ gap: 16 }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        gap: 16, flexWrap: "wrap",
      }}>
        <div>
          <h1 style={{ margin: 0 }}>Fallos pendientes de revisión</h1>
          <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            Decida impugnar o no. Si rechaza la impugnación debe indicar el motivo.
          </div>
        </div>
        {/* feature/fallos-grouped-view. Toggle between the
            flat table (existing) and the new sectioned-by-tipo_fallo view
            Molly asked for ("revisar en orden"). */}
        <FallosViewToggle value={viewMode} onChange={setViewMode} />
      </div>

      {/* feature/estado-filter-pills. Sits above the
          TipoFalloTabs / grouped sections so the bucket counts reflect
          the active scope. Visible in both view modes. */}
      <EstadoFilterPills cases={fallosCases} value={estadoFilter} onChange={setEstadoFilter} />

      {/* feature/tipo-fallo-tabs. Visible only in flat mode.
          Hidden in grouped mode (the sections themselves provide the
          tipo_fallo split, so the tabs would be redundant + risk
          double-filtering). */}
      {viewMode === "flat" && (
        <TipoFalloTabs cases={estadoFilteredCases} value={activeTab} onChange={setActiveTab} />
      )}

      <div className="card tight" style={{ display: "flex", alignItems: "center" }}>
        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>
          {data == null ? "" : `${visibleCases.length} fallo(s)`}
        </span>
      </div>

      {data == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : !visibleCases.length ? (
        <div className="card">
          <Empty title="No hay fallos pendientes de revisión" message="Cuando Reparto registre un fallo, aparecerá aquí." />
        </div>
      ) : viewMode === "grouped" ? (
        <GroupedFallosView
          cases={visibleCases}
          userId={user?.id}
          onOpenCase={setOpenId}
        />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Caso</th><th>Tipo de fallo</th><th>Accionante</th><th>Radicado</th>
                <th>Juzgado</th><th>Fecha de ingreso</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {visibleCases.map(c => (
                <tr key={c.id} onClick={() => setOpenId(c.id)} style={{ cursor: "pointer" }}>
                  <td><strong>{c.case_number}</strong></td>
                  <td><TipoFalloPill value={c.tipo_fallo} missingLabel="—" /></td>
                  <td>{c.accionante_nombre || "—"}</td>
                  <td>{c.radicado}</td>
                  <td>{c.juzgado || "—"}</td>
                  <td>{fmt(c.fecha_ingreso)}</td>
                  {/* Sprint X2 — pipeline estado pill so Molly can scan
                      desfavorables in pendiente_insumos / insumos_solicitados /
                      etc. and prioritize the ones already past handoff. */}
                  <td><EstadoPill c={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CaseDetail
        caseId={openId} open={!!openId}
        defaultTab="decision"
        onClose={() => setOpenId(null)}
        onChanged={load}
      />
    </div>
  );
}

// Re-export the tipo-fallo styles so the historic view + Decisión tab share them.
export { TF_STYLE, TIPO_FALLO_OPTS };
